import React from 'react';
import PropTypes from 'prop-types';
import {
  Picture,
  Button,
  WaveRating,
  TripAdvisorRating,
  GuestRating,
} from '@bwoty-web/styleguide';
import formatPrice from '@bwoty-web/price-formatter';
import MatchingBadges from '../matchingBadges/matchingBadges';
import CardLink from '../cardLink/cardLink';
import { getCloudinarySources } from '../../../utils/imageUtils';
import { getDateFormat } from '../../../utils/date';
import { createAnchor } from '../../../utils/helpers';
import './hotelCard.scss';

const HotelCard = ({
  siteId,
  imageDomain,
  image,
  name,
  url,
  location,
  rating,
  description,
  guestRating,
  tripadvisorRating,
  numberOfAdults,
  numberOfChildren,
  salesPrice,
  originalPrice,
  hasDiscount,
  departureDate,
  departureCode,
  itemClassifications,
  departureAirports,
  labels,
  trackingProduct,
  filters,
  classifications,
}) => {
  const getIncludedText = () => {
    // Build text above price (includedText)
    let numberOfAdultsText = '';
    let numberOfChildrenText = '';
    if (numberOfAdults > 0) {
      numberOfAdultsText =
        numberOfAdults > 1
          ? ` ${numberOfAdults} ${labels.hotelAdults}`
          : ` 1 ${labels.hotelAdult}`;
    }
    if (numberOfChildren > 0) {
      numberOfChildrenText =
        numberOfChildren > 1
          ? ` ${labels.hotelAnd} ${numberOfChildren} ${labels.hotelChildren}`
          : ` ${labels.hotelAnd} 1 ${labels.hotelChild}`;
    }

    const includedText = `${labels.hotelIncludedText}${numberOfAdultsText}${numberOfChildrenText}`;
    return includedText;
  };

  const getDepartureText = () => {
    const departureAirport = departureAirports.find((airport) =>
      airport.value.includes(departureCode),
    )?.name;
    const departureText = departureDate
      ? `${labels.hotelDeparture} ${getDateFormat(departureDate, siteId)} ${
          labels.hotelFrom
        } ${departureAirport}`
      : '';
    return departureText;
  };

  return (
    <div
      id={createAnchor(name)}
      className="vf-result-hotel-card vf-result-card"
    >
      <div className="vf-result-hotel-card__image-container">
        <CardLink anchorName={name} url={url} trackingProduct={trackingProduct}>
          <Picture
            lazy
            className="vf-result-hotel-card__image-container__image"
            sources={[
              ...getCloudinarySources(
                '(max-width: 500px)',
                imageDomain,
                image,
                304,
                456,
              ),
              ...getCloudinarySources(
                '(max-width: 840px)',
                imageDomain,
                image,
                527,
                791,
              ),
              ...getCloudinarySources(
                '(min-width: 841px)',
                imageDomain,
                image,
                304,
                456,
              ),
            ]}
          />
        </CardLink>

        <MatchingBadges itemClassifications={itemClassifications} filters={filters} classifications={classifications} />
      </div>

      <div className="vf-result-hotel-card__info-container">
        <h2 className="vf-result-hotel-card__header">
          <CardLink
            anchorName={name}
            url={url}
            trackingProduct={trackingProduct}
          >
            {name}
          </CardLink>
        </h2>
        <h3 className="vf-result-hotel-card__sub-header">{location}</h3>
        <WaveRating
          className="vf-result-hotel__ving-rating"
          rating={rating}
          size="medium"
        />
        <div className="vf-result-hotel-card__sub-container">
          <p className="vf-result-hotel-card__sub-container__description">
            {description}
          </p>
          <div className="vf-result-hotel-card__sub-container__ratings">
            {parseInt(guestRating, 10) > 0 && (
              <GuestRating
                className="vf-result-hote-card__rating"
                rating={guestRating}
              />
            )}
            {tripadvisorRating && (
              <TripAdvisorRating
                gtm="TripAdvisor"
                gtmProps={null}
                header={null}
                hideOwl={false}
                hideRatingAll
                hideRatingDesktop={false}
                rating={tripadvisorRating}
                responsive={false}
              />
            )}
          </div>
        </div>
      </div>
      <div className="vf-result-hotel-card__bottom-info-container">
        <p className="vf-result-hotel-card__included-text">
          {getIncludedText()}
        </p>
        <p className="vf-result-hotel-card__departure-text">
          {getDepartureText()}
        </p>
        <div className="vf-result-hotel-card__bottom-info-container__price-and-button">
          {salesPrice ? (
            <h2
              className={`vf-result-hotel-card__price ${
                hasDiscount && ' vf-result-hotel-card__price--discounted'
              }`}
            >
              <CardLink
                anchorName={name}
                url={url}
                trackingProduct={trackingProduct}
              >
                {formatPrice(salesPrice, siteId, true)}
              </CardLink>
              {hasDiscount && (
                <span className="vf-result-hotel-card__normal-price">
                  {formatPrice(originalPrice, siteId, true)}
                </span>
              )}
            </h2>
          ) : (
            <p className="vf-result-hotel-card__no-price-text">
              {labels.hotelNoPriceText}
            </p>
          )}

          <CardLink
            anchorName={name}
            url={url}
            trackingProduct={trackingProduct}
          >
            <Button
              theme="primary"
              className="vf-result-hotel-card__primary-button"
              href={url}
            >
              {labels.selectHotel}
            </Button>
          </CardLink>
        </div>
      </div>
    </div>
  );
};

HotelCard.defaultProps = {
  guestRating: null,
  tripadvisorRating: null,
};

HotelCard.propTypes = {
  siteId: PropTypes.number.isRequired,
  imageDomain: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  rating: PropTypes.number.isRequired,
  description: PropTypes.string.isRequired,
  guestRating: PropTypes.number,
  tripadvisorRating: PropTypes.number,
  numberOfAdults: PropTypes.number.isRequired,
  numberOfChildren: PropTypes.number.isRequired,
  salesPrice: PropTypes.number.isRequired,
  originalPrice: PropTypes.number.isRequired,
  hasDiscount: PropTypes.bool.isRequired,
  departureDate: PropTypes.string.isRequired,
  departureCode: PropTypes.string.isRequired,
  itemClassifications: PropTypes.arrayOf(PropTypes.string).isRequired,
  departureAirports: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  labels: PropTypes.shape({
    selectHotel: PropTypes.string.isRequired,
    hotelNoPriceText: PropTypes.string.isRequired,
    hotelIncludedText: PropTypes.string.isRequired,
    hotelAdults: PropTypes.string.isRequired,
    hotelAdult: PropTypes.string.isRequired,
    hotelChildren: PropTypes.string.isRequired,
    hotelChild: PropTypes.string.isRequired,
    hotelFrom: PropTypes.string.isRequired,
    hotelAnd: PropTypes.string.isRequired,
    hotelDeparture: PropTypes.string.isRequired,
  }).isRequired,
  trackingProduct: PropTypes.shape({}).isRequired,
  filters: PropTypes.shape({
    selectedPrimaryClassifications: PropTypes.arrayOf(PropTypes.string),
    selectedSecondaryClassifications: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  classifications: PropTypes.shape({}).isRequired,
};

export default React.memo(HotelCard, (prevProps, newProps) => {
  // only do a rerender if objectID changes
  return prevProps.objectID === newProps.objectID;
});
